import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { CreditCard, Check, ArrowRight } from "lucide-react";

export default function BillingPage() {
  const { lang } = useI18n();
  const nav = useNavigate();
  const [billing, setBilling] = useState(null);
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const [invoices, setInvoices] = useState([]);

  const load = () => api.get("/billing").then((r) => { setBilling(r.data); if (r.data.subscription) setCycle(r.data.subscription.billing_cycle); });
  const loadInvoices = () => api.get("/billing/invoices").then((r) => setInvoices(r.data.invoices || [])).catch(() => setInvoices([]));
  useEffect(() => { load(); loadInvoices(); api.get("/plans").then((r) => setPlans(r.data.plans)); }, []);

  async function downloadPdf(inv) {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("nx_token");
      const res = await fetch(`${backend}/api/billing/invoices/${inv.invoice_number}/pdf`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(lang === "ar" ? "تعذّر تنزيل الفاتورة" : "Could not download invoice");
    }
  }

  // Plan-change flow rewired to the legitimate Tap Payments trial page.
  // The legacy /billing/change-plan endpoint returns 410 in production and
  // never grants an unpaid subscription; we hand the user off to the Plans
  // page which hosts the real Tap Card SDK collection UI.
  const changePlan = (plan_id) => {
    setBusy(true);
    const q = new URLSearchParams({
      plan_id,
      billing_cycle: cycle,
      from: "billing",
    });
    toast.message(lang === "ar"
      ? "جارٍ توجيهك إلى صفحة الدفع الآمن..."
      : "Redirecting to secure Tap Payments...");
    nav(`/app/plans?${q.toString()}`);
  };

  const [cancelling, setCancelling] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function cancelSubscription() {
    setCancelling(true);
    try {
      const res = await api.post("/billing/subscription/cancel", {});
      const until = res.data?.access_until;
      toast.success(lang === "ar"
        ? (until ? `تم إلغاء التجديد. سيبقى وصولك حتى ${new Date(until).toLocaleDateString()}` : "تم إلغاء التجديد التلقائي")
        : (until ? `Cancellation scheduled. Access continues until ${new Date(until).toLocaleDateString()}` : "Auto-renewal cancelled"));
      setConfirmOpen(false);
      await load();
    } catch (e) {
      toast.error(lang === "ar" ? "تعذّر إلغاء الاشتراك" : "Could not cancel subscription");
    } finally { setCancelling(false); }
  }

  async function reactivateSubscription() {
    setCancelling(true);
    try {
      await api.post("/billing/subscription/reactivate", {});
      toast.success(lang === "ar" ? "تمت إعادة تفعيل الاشتراك" : "Subscription reactivated");
      await load();
    } catch (e) {
      toast.error(lang === "ar" ? "تعذّرت إعادة التفعيل" : "Could not reactivate");
    } finally { setCancelling(false); }
  }

  if (!billing) return <AppShell><div className="p-12 text-center text-muted-foreground">Loading…</div></AppShell>;

  const currentPlan = billing.subscription?.plan_id;
  const sub = billing.subscription || {};
  const isCancelled = !!sub.cancel_at_period_end;
  const accessUntil = sub.trial_ends_at || sub.next_billing_at || sub.ends_at;
  const cancellable = !isCancelled && ["trialing","active","pending"].includes((sub.status || "").toLowerCase());

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="font-display text-3xl">{lang === "ar" ? "الفوترة" : "Billing"}</h1>
        <p className="text-muted-foreground text-sm mt-1">{lang === "ar" ? "اشتراكك، الفواتير، وطرق الدفع." : "Your subscription, invoices, and payment methods."}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="nx-card p-5 lg:col-span-2" data-testid="billing-current">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{lang === "ar" ? "خطتك الحالية" : "Current plan"}</div>
          {billing.subscription ? (
            <>
              <div className="flex items-baseline gap-3">
                <div className="font-display text-3xl">{billing.subscription.plan_name}</div>
                <div className="text-muted-foreground">${billing.subscription.amount} / {billing.subscription.billing_cycle}</div>
              </div>
              <div className="mt-4 text-sm grid grid-cols-2 md:grid-cols-4 gap-4">
                <div><div className="text-xs text-muted-foreground">Status</div>
                  <div data-testid="billing-status-value" className={isCancelled ? "text-amber-600" : "text-emerald-500"}>
                    {isCancelled
                      ? (lang === "ar" ? "ملغى — تنتهي في نهاية الفترة" : "Cancelling at period end")
                      : billing.subscription.status}
                  </div>
                </div>
                <div><div className="text-xs text-muted-foreground">Started</div><div>{new Date(billing.subscription.started_at).toLocaleDateString()}</div></div>
                <div><div className="text-xs text-muted-foreground">{isCancelled ? (lang === "ar" ? "الوصول حتى" : "Access until") : "Renews"}</div><div data-testid="billing-access-until">{accessUntil ? new Date(accessUntil).toLocaleDateString() : "—"}</div></div>
                <div><div className="text-xs text-muted-foreground">Payment</div><div>{(billing.subscription.payment_status || "—").toString()}</div></div>
              </div>
              <div className="mt-5 flex items-center gap-3">
                {cancellable && (
                  <button
                    data-testid="billing-cancel-btn"
                    onClick={() => setConfirmOpen(true)}
                    disabled={cancelling}
                    className="text-sm px-4 py-2 rounded-sm border border-border hover:border-destructive hover:text-destructive transition"
                  >
                    {lang === "ar" ? "إلغاء الاشتراك" : "Cancel subscription"}
                  </button>
                )}
                {isCancelled && (
                  <button
                    data-testid="billing-reactivate-btn"
                    onClick={reactivateSubscription}
                    disabled={cancelling}
                    className="text-sm px-4 py-2 rounded-sm border border-primary text-primary hover:bg-primary/5 transition"
                  >
                    {cancelling ? (lang === "ar" ? "جاري..." : "Working...") : (lang === "ar" ? "إعادة تفعيل الاشتراك" : "Reactivate subscription")}
                  </button>
                )}
              </div>
            </>
          ) : <div className="text-muted-foreground text-sm">No active subscription.</div>}
        </div>

        <div className="nx-card p-5" data-testid="billing-payment-method">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{lang === "ar" ? "طريقة الدفع" : "Payment method"}</div>
          <div className="flex items-center gap-3 mt-2">
            <div className="w-11 h-8 rounded-sm bg-muted flex items-center justify-center"><CreditCard className="w-4 h-4 text-muted-foreground" /></div>
            <div>
              <div className="font-medium text-sm">{billing.payment_method.kind}</div>
              <div className="text-xs text-muted-foreground">{billing.payment_method.label}</div>
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground mt-4">{lang === "ar" ? "المدفوعات تُدار عبر Tap Payments بواجهة 3DS آمنة." : "Payments are handled via Tap Payments with 3DS-secured card collection."}</p>
        </div>
      </div>

      <div className="nx-card p-5 mb-6" data-testid="billing-plans">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">{lang === "ar" ? "تغيير الخطة" : "Change plan"}</div>
          <div className="inline-flex bg-background border border-border rounded-sm p-1">
            <button data-testid="billing-cycle-monthly" onClick={() => setCycle("monthly")} className={`px-3 py-1.5 text-xs rounded-sm ${cycle === "monthly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{lang === "ar" ? "شهري" : "Monthly"}</button>
            <button data-testid="billing-cycle-year" onClick={() => setCycle("yearly")} className={`px-3 py-1.5 text-xs rounded-sm ${cycle === "yearly" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{lang === "ar" ? "سنوي" : "Yearly"}</button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((p) => {
            const isCurrent = p.id === currentPlan && cycle === billing.subscription?.billing_cycle;
            return (
              <div key={p.id} data-testid={`billing-plan-${p.id}`} className={`p-5 border rounded-sm ${isCurrent ? "border-primary ring-1 ring-primary" : "border-border"}`}>
                <div className="font-display text-xl">{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1"><span className="font-display text-3xl">${p.prices[cycle]}</span><span className="text-xs text-muted-foreground">/{cycle}</span></div>
                <ul className="mt-3 space-y-1.5 text-xs">
                  {p.features.slice(0, 4).map((f, i) => <li key={i} className="flex gap-1"><Check className="w-3 h-3 text-primary shrink-0 mt-0.5" /><span>{f}</span></li>)}
                </ul>
                <button data-testid={`billing-select-${p.id}`} onClick={() => changePlan(p.id)} disabled={busy || isCurrent}
                  className={`mt-4 w-full text-sm py-2 rounded-sm ${isCurrent ? "bg-muted text-muted-foreground" : "nx-btn-primary"}`}>
                  {isCurrent ? (lang === "ar" ? "الخطة الحالية" : "Current plan") : (lang === "ar" ? "اختر" : "Select")}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div className="nx-card p-5" data-testid="billing-invoices">
        <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{lang === "ar" ? "الفواتير" : "Invoices"}</div>
        {invoices.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">
            {lang === "ar" ? "لا توجد فواتير حتى الآن." : "No invoices yet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="text-start py-2">{lang === "ar" ? "رقم الفاتورة" : "Invoice #"}</th>
                  <th className="text-start py-2">{lang === "ar" ? "التاريخ" : "Date"}</th>
                  <th className="text-start py-2">{lang === "ar" ? "الخطة" : "Plan"}</th>
                  <th className="text-end py-2">{lang === "ar" ? "الصافي" : "Subtotal"}</th>
                  <th className="text-end py-2">{lang === "ar" ? "الضريبة" : "VAT"}</th>
                  <th className="text-end py-2">{lang === "ar" ? "الإجمالي" : "Total"}</th>
                  <th className="text-end py-2">{lang === "ar" ? "الإجراء" : ""}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.invoice_number} data-testid={`invoice-row-${inv.invoice_number}`} className="border-t border-border/40">
                    <td className="py-2 font-mono text-xs">{inv.invoice_number}</td>
                    <td className="py-2">{new Date(inv.invoice_date).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US")}</td>
                    <td className="py-2">{inv.plan_name || inv.plan_id}</td>
                    <td className="py-2 text-end tabular-nums">{Number(inv.subtotal).toFixed(2)}</td>
                    <td className="py-2 text-end tabular-nums text-muted-foreground">
                      {Number(inv.vat_amount).toFixed(2)} <span className="text-xs">({inv.vat_rate}%)</span>
                    </td>
                    <td className="py-2 text-end font-medium tabular-nums">{Number(inv.total).toFixed(2)} {inv.currency}</td>
                    <td className="py-2 text-end">
                      <button
                        data-testid={`invoice-pdf-${inv.invoice_number}`}
                        onClick={() => downloadPdf(inv)}
                        className="text-xs underline hover:text-foreground"
                      >
                        {lang === "ar" ? "تنزيل PDF" : "Download PDF"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-3 text-[11px] text-muted-foreground">
          {lang === "ar"
            ? "جميع المبالغ بالريال السعودي وتشمل ضريبة القيمة المضافة عند الاقتضاء."
            : "All amounts are in SAR and include VAT where applicable."}
        </p>
      </div>

      <div data-testid="billing-legal-links" className="mt-6 text-xs text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-2">
        <Link to="/refund" className="underline hover:text-foreground">Refund policy</Link>
        <Link to="/cancellation" className="underline hover:text-foreground">Cancellation</Link>
        <Link to="/terms" className="underline hover:text-foreground">Terms</Link>
        <Link to="/support" className="underline hover:text-foreground">Contact support</Link>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" data-testid="billing-cancel-modal" onClick={() => !cancelling && setConfirmOpen(false)}>
          <div className="nx-card p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-2">{lang === "ar" ? "تأكيد إلغاء الاشتراك" : "Cancel subscription?"}</h3>
            <p className="text-sm text-muted-foreground mb-2">
              {lang === "ar"
                ? "لن يتم تجديد اشتراكك تلقائياً. ستحتفظ بالوصول لجميع الميزات المدفوعة حتى:"
                : "Your subscription will not renew. You keep full access to paid features until:"}
            </p>
            <p className="text-sm font-medium mb-4" data-testid="billing-cancel-modal-until">
              {accessUntil ? new Date(accessUntil).toLocaleDateString() : "—"}
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              {lang === "ar"
                ? "يمكنك إعادة التفعيل في أي وقت قبل نهاية الفترة."
                : "You can reactivate any time before the period ends."}
            </p>
            <div className="flex items-center justify-end gap-2">
              <button data-testid="billing-cancel-modal-cancel" onClick={() => setConfirmOpen(false)} disabled={cancelling} className="text-sm px-4 py-2 rounded-sm border border-border hover:bg-muted transition">
                {lang === "ar" ? "الرجوع" : "Go back"}
              </button>
              <button data-testid="billing-cancel-modal-confirm" onClick={cancelSubscription} disabled={cancelling} className="text-sm px-4 py-2 rounded-sm bg-destructive text-destructive-foreground hover:opacity-90 transition disabled:opacity-60">
                {cancelling ? (lang === "ar" ? "جاري..." : "Working...") : (lang === "ar" ? "نعم، ألغِ الاشتراك" : "Yes, cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
